/**
 * Casting (workspace-client.md §2n): every message has a sender, because a message from nobody is a UI
 * panel wearing a message costume (§2m).
 *
 * This is ONE TABLE, which is the point. The terminal's rule is that every player affordance derives
 * from `SESSION_COMMANDS`; the messenger's equivalent is that every FACE and every NAME derives from
 * here, so a new voice cannot be introduced anywhere in the render layer without appearing in this file
 * and in its test.
 *
 * Where the names come from matters. A character's name is AUTHORED CONTENT
 * (`backend/content/npcs/*.yaml`) and reaches this client on `seats[].character` - the client never
 * invents one, and a seat the scenario wrote no character for renders as an open role rather than as a
 * person with a made-up name (§1b: colleagues, never vacancies - but also never fictions the content
 * did not write).
 *
 * The FACES are the one thing this table owns, keyed by that authored name. A name with no face gets a
 * monogram, which is what every real messenger does and costs nothing when a scenario adds a character
 * before its portrait is generated.
 */
import { t } from './i18n.ts';

/** Who a message is from, ready to render. */
export interface CastMember {
  /** Stable identity for the DOM and for tests. */
  id: string;
  /** What the room calls them. */
  name: string;
  /** Path to a face, or null → the monogram fallback. */
  avatar: string | null;
  /** A person, an account, or a machine. Machines never get faces (§5a). */
  kind: 'person' | 'bot' | 'org';
  /** A one-word subtitle for the member list; null when there is nothing worth saying. */
  subtitle: string | null;
}

/**
 * Authored name → generated face. The names are transcribed from `backend/content/npcs/*.yaml`; the
 * faces from `clients/workspace/assets/` (one saved `workspace-avatar` style, §5a). A name that drifts
 * in content simply loses its portrait and gains a monogram - it never renders as the wrong person.
 */
const FACES: Record<string, string> = {
  // signal-triage
  'Daniel Okonkwo': 'daniel-okonkwo',
  'Даниэль Оконкво': 'daniel-okonkwo',
  'Camila Ferreira': 'camila-ferreira',
  'Камила Феррейра': 'camila-ferreira',
  // the rest of the packaged decks
  'Hiroshi Tanabe': 'hiroshi-tanabe',
  'Marta Kowalski': 'marta-kowalski',
  'Priya Raghunathan': 'priya-raghunathan',
  'Tomas Ruiz': 'tomas-ruiz',
  'Ade Balogun': 'ade-balogun',
  'Ingrid Sorensen': 'ingrid-sorensen',
  'Wei Chen': 'wei-chen',
  'Nadia Haddad': 'nadia-haddad',
  'Sofia Marchetti': 'sofia-marchetti',
  'Oliver Brandt': 'oliver-brandt',
  'Emre Demir': 'emre-demir',
};

/**
 * Where the faces are served from. The app's base path is compiled in by Vite, and asset URLs have to
 * carry it or every portrait 404s the moment the site owns the root and the app lives under /office/.
 */
function assetBase(): string {
  const env = (import.meta as unknown as { env?: { BASE_URL?: string } }).env;
  return env?.BASE_URL ?? '/';
}

function faceFor(name: string): string | null {
  const file = FACES[name];
  return file ? `${assetBase()}avatars/${file}.png` : null;
}

/** The colleague who holds a role, from the authored name the projection carries. */
export function character(name: string, subtitle: string | null = null): CastMember {
  return { id: `character:${name}`, name, avatar: faceFor(name), kind: 'person', subtitle };
}

/** A human at the table. Their face is their initials: we do not generate portraits of real people. */
export function human(playerId: string, name: string, subtitle: string | null = null): CastMember {
  return { id: `player:${playerId}`, name, avatar: null, kind: 'person', subtitle };
}

/**
 * The monitoring bot (§2n). Incidents arrive from IT - terse, ugly, alarming - and then people react in
 * the thread. That three-beat scene is what the medium does natively and what a bare event card never
 * could; casting it is the whole trick.
 */
export function monitoringBot(): CastMember {
  return {
    id: 'bot:monitoring',
    name: t('incident.sender'),
    avatar: `${assetBase()}avatars/monitoring-bot.png`,
    kind: 'bot',
    subtitle: null,
  };
}

/**
 * THE NARRATOR IS DECOMPOSED (§2n, round 5). Slack does not have a narrator, and a narrator in Slack is
 * a surveillance camera - a voice that sees everything and belongs to nobody. The first build of this
 * client shipped one anyway; this is the correction.
 *
 * Three apps replace it, each a thing a real workspace actually contains, and the mapping is by what the
 * text IS rather than by where it came from:
 *
 * - **meeting notes** - a scene being read out (`evt.scene.narrated`). "standup notes: …"
 * - **deploys** - what a beat DID (`evt.game.narration`): the result line for a committed action, with
 *   the people reacting to it in the thread underneath rather than being spoken for.
 * - **the digest** - the day's summary and the re-entry catch-up.
 *
 * None of them is omniscient, and none of them speaks for a person. Where a PERSON should be speaking,
 * the NPC line is the message and the bot is not involved at all.
 */
export type SystemVoice = 'meeting' | 'deploy' | 'digest' | 'survey';

const VOICES: Record<
  SystemVoice,
  { key: 'bot.meeting' | 'bot.deploy' | 'bot.digest' | 'bot.survey'; file: string }
> = {
  meeting: { key: 'bot.meeting', file: 'meeting-bot' },
  deploy: { key: 'bot.deploy', file: 'deploy-bot' },
  digest: { key: 'bot.digest', file: 'digest-bot' },
  survey: { key: 'bot.survey', file: 'survey-bot' },
};

/**
 * The sender for a voice the SERVER named (§2n).
 *
 * The casting used to be inferred here from which payload field a beat carried, which meant every event
 * narration was attributed to the deploy bot - including an incident arriving, so the thing that ships
 * code announced the fire. The engine knows which kind of beat it was; the server sends it, and this
 * only turns that name into a face.
 *
 * An unknown or absent voice falls back to the digest bot: the workspace's own bookkeeping, which
 * belongs to no person and therefore mis-attributes nobody.
 */
export function voiceSender(voice: string | undefined): CastMember {
  if (voice === 'monitoring') return monitoringBot();
  return systemBot(voice && voice in VOICES ? (voice as SystemVoice) : 'digest');
}

/** One of the workspace's apps, by what it is for. */
export function systemBot(voice: SystemVoice): CastMember {
  const cast = VOICES[voice];
  return {
    id: `bot:${voice}`,
    name: t(cast.key),
    avatar: `${assetBase()}avatars/${cast.file}.png`,
    kind: 'bot',
    subtitle: null,
  };
}

/**
 * An OUTSIDE-WORLD stakeholder (npc-llm-layer §7.5b). Not a colleague and not a bot: the company on the
 * other end of the call, who has a name when the roster authored a character for it and the
 * organisation's own name when it did not.
 *
 * Kept distinct from `character()` on purpose. §7.5b's naming correction is that the two stakeholder
 * kinds had been blurred - a seat with a stakeholder-shaped profile is a colleague, and this is not one.
 * A line from `acme` arriving as if a teammate said it would undo the distinction the outside world
 * exists to draw.
 */
export function stakeholder(key: string, name: string | null): CastMember {
  return {
    id: `stakeholder:${key}`,
    name: name ?? key,
    avatar: faceFor(name ?? ''),
    kind: 'org',
    subtitle: t('cast.outsideWorld'),
  };
}

/**
 * The onboarding buddy (§1a). A named character with a face, and their lines are SCRIPTED before they
 * are ever a model call - which is what makes the first ninety seconds survive a dead provider and cost
 * nothing.
 *
 * Deliberately client-side rather than an authored NPC profile: the backend's profiles are scoped to a
 * scenario and a role, and the buddy belongs to the CLIENT's onboarding, not to any one deck. Recorded
 * as a correction in workspace-client.md §2n.
 */
export function buddy(): CastMember {
  return {
    id: 'character:buddy',
    name: t('buddy.name'),
    avatar: `${assetBase()}avatars/nour-amrani.png`,
    kind: 'person',
    subtitle: t('buddy.role'),
  };
}

/** The monogram a faceless person gets: one or two initials, the way every messenger does it. */
export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }

  const first = [...parts[0]!][0] ?? '?';
  const second = parts.length > 1 ? ([...parts[parts.length - 1]!][0] ?? '') : '';
  return (first + second).toUpperCase();
}

/** Exported for the casting test: no face may be referenced that the app does not ship. */
export const FACE_FILES: readonly string[] = [
  ...new Set([
    ...Object.values(FACES),
    ...Object.values(VOICES).map((v) => v.file),
    'monitoring-bot',
    'nour-amrani',
  ]),
];
